import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, Smartphone, FileDown } from "lucide-react";

const SECURITY_PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/security-overview.pdf`;

export function SecurityMfa() {
  const [enabled, setEnabled] = useState(null);
  const [enforced, setEnforced] = useState(false);
  const [mode, setMode] = useState("idle"); // idle | enroll | disable
  const [enroll, setEnroll] = useState(null); // {qr, manual_key}
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/mfa/status");
      setEnabled(data.mfa_enabled);
      setEnforced(data.enforced);
      return data;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  useEffect(() => { loadStatus(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mfa/enroll");
      setEnroll(data); setMode("enroll"); setCode("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/mfa/enable", { code });
      toast.success("Two-step verification is now ON for this admin account");
      setMode("idle"); setEnroll(null); setCode(""); loadStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmDisable = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/mfa/disable", { password: pwd, code });
      toast.success("Two-step verification turned off");
      setMode("idle"); setPwd(""); setCode(""); loadStatus();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // When MFA is mandatory but not yet set up, open the enrolment flow automatically.
  useEffect(() => {
    if (enforced && enabled === false && mode === "idle" && !enroll) { startEnroll(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforced, enabled]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 mt-6" data-testid="admin-security-mfa">
      <div className="flex items-center gap-2 flex-wrap">
        {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
        <h2 className="font-display text-lg font-bold text-slate-900">Two-step verification (authenticator app)</h2>
        <a
          href={SECURITY_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-security-overview"
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-primary hover:border-primary/40 transition-colors"
        >
          <FileDown className="w-3.5 h-3.5" /> Download security overview (PDF)
        </a>
      </div>
      <p className="text-sm text-slate-500 mt-1">
        Adds an authenticator-app code (Google Authenticator, Microsoft Authenticator, 1Password, etc.) on top of your admin password — the recommended control for privileged accounts.
      </p>

      {enforced && enabled === false && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="mfa-required-banner">
          <b>Action required:</b> two-step verification is <b>mandatory</b> for administrator accounts. Please complete the setup below now — scan the QR code with your authenticator app and enter the 6-digit code.
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${enabled ? "bg-emerald-100 text-emerald-700" : enforced ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid="mfa-status-badge">
          {enabled === null ? "…" : enabled ? "Enabled" : enforced ? "Required — not set up" : "Not enabled"}
        </span>
        {mode === "idle" && enabled === false && (
          <Button data-testid="mfa-start-enroll" onClick={startEnroll} disabled={busy} className="h-9">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Smartphone className="w-4 h-4 mr-1.5" /> Set up</>}
          </Button>
        )}
        {mode === "idle" && enabled === true && (
          <Button data-testid="mfa-start-disable" variant="outline" onClick={() => { setMode("disable"); setCode(""); setPwd(""); }} className="h-9">
            Turn off
          </Button>
        )}
      </div>

      {mode === "enroll" && enroll && (
        <form onSubmit={confirmEnable} className="mt-5 border-t border-slate-100 pt-5 grid sm:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="text-center">
            <img src={enroll.qr} alt="Authenticator QR code" width={168} height={168} className="rounded-lg border border-slate-200" data-testid="mfa-qr" />
            <div className="text-[11px] text-slate-400 mt-2">Or enter this key manually:</div>
            <code className="text-xs font-num text-slate-600 break-all">{enroll.manual_key}</code>
          </div>
          <div>
            <ol className="text-sm text-slate-600 list-decimal ml-4 space-y-1 mb-3">
              <li>Open your authenticator app and choose <b>Scan a QR code</b>.</li>
              <li>Scan the code (or type the key), then enter the 6-digit code it shows.</li>
            </ol>
            <label className="text-sm font-medium text-slate-700">Code from app</label>
            <Input data-testid="mfa-enable-code" inputMode="numeric" maxLength={6} autoFocus value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1.5 h-11 max-w-[200px] tracking-[0.4em] text-center font-num" placeholder="000000" />
            <div className="flex gap-2 mt-3">
              <Button data-testid="mfa-confirm-enable" type="submit" disabled={busy || code.length !== 6} className="h-10">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enable"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setMode("idle"); setEnroll(null); }} className="h-10">Cancel</Button>
            </div>
          </div>
        </form>
      )}

      {mode === "disable" && (
        <form onSubmit={confirmDisable} className="mt-5 border-t border-slate-100 pt-5 space-y-3 max-w-sm">
          <p className="text-sm text-slate-600">Confirm your password and a current authenticator code to turn two-step off.</p>
          <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="h-11" placeholder="Admin password" data-testid="mfa-disable-pwd" />
          <Input inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-11 tracking-[0.4em] text-center font-num" placeholder="000000" data-testid="mfa-disable-code" />
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || code.length !== 6 || !pwd} variant="destructive" className="h-10" data-testid="mfa-confirm-disable">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Turn off"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setMode("idle")} className="h-10">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
